import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { teamMembers, stats } from '../data/mock';
import { Mail, Phone, Award, Users, TrendingUp, Clock } from 'lucide-react';

const About = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-r from-[#E2725B] to-[#FF914D] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            ABOUT ELITE REAL ESTATE
          </h1>
          <p className="text-xl md:text-2xl leading-relaxed">
            London's premier real estate agency, connecting discerning clients with exceptional properties since 2009
          </p>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-8" style={{ color: '#2E2E2E' }}>
            Our Mission
          </h2>
          <p className="text-xl text-gray-700 leading-relaxed mb-8">
            We connect people with their perfect home by providing unparalleled expertise, personalized service, and access to London's most exclusive properties. Our commitment to excellence and deep market knowledge ensures every client receives the exceptional experience they deserve.
          </p>
          <Button 
            className="bg-gradient-to-r from-[#E2725B] to-[#FF914D] hover:from-[#d65a43] hover:to-[#e8823d] text-white px-8 py-4 text-lg font-semibold"
            asChild
          >
            <Link to="/contact">Work With Us</Link>
          </Button>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20" style={{ backgroundColor: '#FFF5E6' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4" style={{ color: '#2E2E2E' }}>
              Our Track Record
            </h2>
            <p className="text-xl text-gray-600">
              Numbers that speak to our success and commitment
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-5xl md:text-6xl font-bold mb-4" style={{ color: '#E2725B' }}>
                  {stat.value}
                </div>
                <div className="text-lg font-medium" style={{ color: '#2E2E2E' }}>
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4" style={{ color: '#2E2E2E' }}>
              What Sets Us Apart
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Our unique approach and commitment to excellence make us London's trusted real estate partner
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center group">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-r from-[#E2725B] to-[#FF914D] flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <Award className="h-10 w-10 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-4" style={{ color: '#2E2E2E' }}>
                Award-Winning Service
              </h3>
              <p className="text-gray-600">
                Recognized industry leaders with multiple awards for outstanding client service and market expertise
              </p>
            </div>
            
            <div className="text-center group">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-r from-[#E2725B] to-[#FF914D] flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <Users className="h-10 w-10 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-4" style={{ color: '#2E2E2E' }}>
                Expert Team
              </h3>
              <p className="text-gray-600">
                Highly qualified professionals with deep local knowledge and years of experience in luxury properties
              </p>
            </div>
            
            <div className="text-center group">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-r from-[#E2725B] to-[#FF914D] flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <TrendingUp className="h-10 w-10 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-4" style={{ color: '#2E2E2E' }}>
                Market Intelligence
              </h3>
              <p className="text-gray-600">
                Cutting-edge market analysis and insights to help you make informed property decisions
              </p>
            </div>
            
            <div className="text-center group">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-r from-[#E2725B] to-[#FF914D] flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <Clock className="h-10 w-10 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-4" style={{ color: '#2E2E2E' }}>
                24/7 Support
              </h3>
              <p className="text-gray-600">
                Round-the-clock availability to ensure you never miss an opportunity or have an unanswered question
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20" style={{ backgroundColor: '#FFF5E6' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4" style={{ color: '#2E2E2E' }}>
              Meet Our Team
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Dedicated professionals committed to finding you the perfect property
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {teamMembers.map((member) => (
              <Card key={member.id} className="text-center hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-6">
                  <div className="relative mb-6">
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-32 h-32 rounded-full mx-auto object-cover"
                    />
                  </div>
                  <h3 className="text-xl font-bold mb-2" style={{ color: '#2E2E2E' }}>
                    {member.name}
                  </h3>
                  <p className="text-[#E2725B] font-medium mb-4">
                    {member.position}
                  </p>
                  <p className="text-gray-600 text-sm mb-6 leading-relaxed">
                    {member.bio}
                  </p>
                  <div className="flex justify-center space-x-4">
                    <a 
                      href={`mailto:${member.email}`}
                      className="text-gray-600 hover:text-[#E2725B] transition-colors"
                    >
                      <Mail className="h-5 w-5" />
                    </a>
                    <a 
                      href={`tel:${member.phone}`}
                      className="text-gray-600 hover:text-[#E2725B] transition-colors"
                    >
                      <Phone className="h-5 w-5" />
                    </a>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Company History */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4" style={{ color: '#2E2E2E' }}>
              Our Story
            </h2>
          </div>
          
          <div className="space-y-12">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="md:w-1/2">
                <h3 className="text-2xl font-bold mb-4" style={{ color: '#E2725B' }}>
                  Founded in 2009
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Elite Real Estate was established with a simple mission: to revolutionize the London property market through exceptional service and unmatched expertise. From our modest beginnings in Mayfair, we've grown to become one of London's most trusted real estate agencies.
                </p>
              </div>
              <div className="md:w-1/2">
                <div className="bg-gradient-to-r from-[#E2725B] to-[#FF914D] text-white p-8 rounded-lg">
                  <h4 className="text-xl font-bold mb-4">Our Values</h4>
                  <ul className="space-y-2">
                    <li>• Integrity in every transaction</li>
                    <li>• Personalized client service</li>
                    <li>• Market expertise and innovation</li>
                    <li>• Long-term relationships</li>
                  </ul>
                </div>
              </div>
            </div>
            
            <div className="flex flex-col md:flex-row-reverse items-center gap-8">
              <div className="md:w-1/2">
                <h3 className="text-2xl font-bold mb-4" style={{ color: '#E2725B' }}>
                  Growing Success
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Today, we've helped over 1000 families find their dream homes and assisted countless investors in building profitable property portfolios. Our success is measured not just in transactions, but in the lasting relationships we've built with our clients.
                </p>
              </div>
              <div className="md:w-1/2">
                <div className="bg-[#FFF5E6] p-8 rounded-lg border border-[#E2725B]/20">
                  <h4 className="text-xl font-bold mb-4" style={{ color: '#2E2E2E' }}>
                    Looking Forward
                  </h4>
                  <p className="text-gray-700">
                    As London continues to evolve, so do we. We're constantly innovating our services and expanding our expertise to meet the changing needs of the property market while maintaining the personal touch that sets us apart.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#E2725B] to-[#FF914D] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Begin Your Property Journey?
          </h2>
          <p className="text-xl mb-8">
            Let our experienced team help you find your perfect home in London
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              className="bg-white text-[#E2725B] hover:bg-gray-100 px-8 py-4 text-lg font-semibold"
              asChild
            >
              <Link to="/contact">Get Started Today</Link>
            </Button>
            <Button 
              variant="outline" 
              className="border-2 border-white text-white hover:bg-white hover:text-[#E2725B] px-8 py-4 text-lg font-semibold"
              asChild
            >
              <Link to="/properties">View Properties</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;